import { useQuery } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'
import type { Partnership, PartnershipInvitation } from '@/admin/types/organisations'

interface PaginatedPartnerships {
  results: Partnership[]
  count: number
}

/**
 * A tenant-defined PartnershipAttribute *definition* (not a value) as served by
 * `GET /organisations/{orgUuid}/partnership-attributes/`. Drives the per-attribute
 * columns on the partners list: the header is `display_name`, the cell reads the
 * value out of a row's `attribute_summary[code_name]` and formats it by `field_type`.
 */
export interface PartnershipAttributeDefinition {
  uuid: string
  code_name: string
  display_name: string
  description: string
  field_type: string
  configuration_data: Record<string, unknown>
  required: boolean
  editable: boolean
  order: number
}

/**
 * The tenant's partnership attribute schema, ordered by `order` then `code_name`
 * (the backend already sorts). Used to render one toggleable column per attribute
 * on the partners table. Returns [] for tenants that define no PartnershipAttribute.
 */
export function usePartnershipAttributes() {
  const { orgUuid } = useOrg()
  return useQuery<PartnershipAttributeDefinition[]>({
    queryKey: ['partnership-attributes', orgUuid],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/partnership-attributes/`)
        .then((r) => {
          // View wraps the list in APIResponse.success -> `{ result: [...] }`.
          // Tolerate a bare array / DRF `results` too, so the hook survives an
          // envelope change without silently blanking every attribute column.
          const data = r.data
          if (Array.isArray(data)) return data as PartnershipAttributeDefinition[]
          if (Array.isArray(data?.result)) return data.result as PartnershipAttributeDefinition[]
          if (Array.isArray(data?.results)) return data.results as PartnershipAttributeDefinition[]
          return [] as PartnershipAttributeDefinition[]
        }),
    enabled: !!orgUuid,
  })
}

export function usePartnerships(params?: Record<string, string>) {
  const { orgUuid } = useOrg()
  return useQuery<PaginatedPartnerships>({
    queryKey: ['partnerships', orgUuid, params],
    queryFn: () =>
      api.get(`/organisations/${orgUuid}/partnerships/`, { params }).then(r => r.data),
    enabled: !!orgUuid,
  })
}

export function usePartnershipDetail(partnershipUuid: string | null) {
  const { orgUuid } = useOrg()
  return useQuery<Partnership>({
    queryKey: ['partnership-detail', orgUuid, partnershipUuid],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/partnerships/${partnershipUuid}/`)
        .then((r) => r.data),
    enabled: !!orgUuid && !!partnershipUuid,
    staleTime: 30_000,
  })
}

export function usePartnershipInvitations() {
  const { orgUuid } = useOrg()
  return useQuery<PartnershipInvitation[]>({
    queryKey: ['partnership-invitations', orgUuid],
    queryFn: () =>
      api.get(`/organisations/${orgUuid}/partnership-invitations/`).then(r => r.data.results ?? r.data),
    enabled: !!orgUuid,
  })
}
